/**
 * Hooks LeadBeton Sprint 2 (Modules 4 Gâchées · 5 QC · 6 Parc).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  operationsApi,
  type CreateEprouvettePayload,
  type CreateGacheePayload,
  type EprouvetteListParams,
  type GacheeListParams,
  type MouvementParcListParams,
  type ParcListParams,
  type PlacerParcPayload,
  type ReserverLotPayload,
  type SortirLotPayload,
  type TestEprouvettePayload,
  type TransfertParcPayload,
  type UpdateEprouvettePayload,
  type UpdateGacheePayload,
} from '@/api/operations';

const GACHEES_KEY    = ['operations', 'beton', 'gachees']     as const;
const EPROUV_KEY     = ['operations', 'beton', 'eprouvettes'] as const;
const PARC_KEY       = ['operations', 'beton', 'parc']        as const;
const PARC_MVT_KEY   = ['operations', 'beton', 'parc', 'mouvements'] as const;
const DASHBOARD_QC_KEY = ['operations', 'beton', 'dashboard-qc']    as const;

// ── Gâchées ────────────────────────────────────────────────────────────────

export const useGachees = (params: GacheeListParams = {}) =>
  useQuery({
    queryKey:  [...GACHEES_KEY, params],
    queryFn:   () => operationsApi.beton.listGachees(params),
    staleTime: 30_000,
  });

export const useGachee = (id: string | undefined) =>
  useQuery({
    queryKey:  [...GACHEES_KEY, id],
    queryFn:   () => operationsApi.beton.getGachee(id!),
    enabled:   !!id,
    staleTime: 15_000,
  });

export const useCreateGachee = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateGacheePayload) => operationsApi.beton.createGachee(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: GACHEES_KEY }),
  });
};

export const useUpdateGachee = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateGacheePayload }) =>
      operationsApi.beton.updateGachee(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: GACHEES_KEY }),
  });
};

export const useValiderGachee = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.validerGachee(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: GACHEES_KEY }),
  });
};

export const useDeleteGachee = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteGachee(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: GACHEES_KEY }),
  });
};

// ── Éprouvettes ───────────────────────────────────────────────────────────

export const useEprouvettes = (params: EprouvetteListParams = {}) =>
  useQuery({
    queryKey:  [...EPROUV_KEY, params],
    queryFn:   () => operationsApi.beton.listEprouvettes(params),
    staleTime: 30_000,
  });

export const useEprouvette = (id: string | undefined) =>
  useQuery({
    queryKey:  [...EPROUV_KEY, id],
    queryFn:   () => operationsApi.beton.getEprouvette(id!),
    enabled:   !!id,
    staleTime: 15_000,
  });

export const useCreateEprouvette = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateEprouvettePayload) => operationsApi.beton.createEprouvette(payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: EPROUV_KEY });
      qc.invalidateQueries({ queryKey: DASHBOARD_QC_KEY });
    },
  });
};

export const useTestEprouvette = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TestEprouvettePayload }) =>
      operationsApi.beton.testEprouvette(id, payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: EPROUV_KEY });
      qc.invalidateQueries({ queryKey: DASHBOARD_QC_KEY });
    },
  });
};

export const useEcarterEprouvette = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif?: string }) =>
      operationsApi.beton.ecarterEprouvette(id, motif),
    onSuccess:  () => qc.invalidateQueries({ queryKey: EPROUV_KEY }),
  });
};

export const useUpdateEprouvette = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateEprouvettePayload }) =>
      operationsApi.beton.updateEprouvette(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: EPROUV_KEY }),
  });
};

export const useDeleteEprouvette = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteEprouvette(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: EPROUV_KEY }),
  });
};

export const useDashboardQC = () =>
  useQuery({
    queryKey:           DASHBOARD_QC_KEY,
    queryFn:            () => operationsApi.beton.getDashboardQC(),
    staleTime:          60_000,
    refetchInterval:    120_000,
  });

// ── Parc ─────────────────────────────────────────────────────────────────

export const useParc = (params: ParcListParams = {}) =>
  useQuery({
    queryKey:  [...PARC_KEY, params],
    queryFn:   () => operationsApi.beton.listParc(params),
    staleTime: 30_000,
  });

export const useInventaireParc = () =>
  useQuery({
    queryKey:        [...PARC_KEY, 'inventaire'] as const,
    queryFn:         () => operationsApi.beton.getInventaireParc(),
    staleTime:       30_000,
    refetchInterval: 120_000,
  });

export const useMouvementsParc = (params: MouvementParcListParams = {}) =>
  useQuery({
    queryKey:  [...PARC_MVT_KEY, params],
    queryFn:   () => operationsApi.beton.listMouvementsParc(params),
    staleTime: 30_000,
  });

const invalidateParc = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: PARC_KEY });
  qc.invalidateQueries({ queryKey: ['operations', 'beton', 'lots'] }); // les lots sont aussi exposés via le module lots
};

export const usePlacerLotParc = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lotId, payload }: { lotId: string; payload: PlacerParcPayload }) =>
      operationsApi.beton.placerLotParc(lotId, payload),
    onSuccess: () => invalidateParc(qc),
  });
};

export const useTransfererLotParc = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lotId, payload }: { lotId: string; payload: TransfertParcPayload }) =>
      operationsApi.beton.transfererLotParc(lotId, payload),
    onSuccess: () => invalidateParc(qc),
  });
};

export const useReserverLotParc = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lotId, payload }: { lotId: string; payload: ReserverLotPayload }) =>
      operationsApi.beton.reserverLotParc(lotId, payload),
    onSuccess: () => invalidateParc(qc),
  });
};

export const useLibererLotParc = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lotId: string) => operationsApi.beton.libererLotParc(lotId),
    onSuccess: () => invalidateParc(qc),
  });
};

export const useSortirLotParc = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lotId, payload }: { lotId: string; payload?: SortirLotPayload }) =>
      operationsApi.beton.sortirLotParc(lotId, payload ?? {}),
    onSuccess: () => invalidateParc(qc),
  });
};
